import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';
import { commitMutation, graphql } from 'react-relay';
import { GraphError } from 'types/general';
import environment from 'api/environment';
import Button from 'sharedComponents/core/Input/Button';
import Icon from 'sharedComponents/core/Icon';

const useStyles = createUseStyles((theme: Theme) => ({
  input: {
    display: 'none'
  },
  profilePicContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '30%',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(3) //"5%"
  },
  profilePic: {
    height: 100,
    width: 100,
    borderRadius: 100
  },
  editPicPencilIcon: {
    marginBottom: -30,
    marginRight: -65,
    zIndex: 15
  },
  editPhotoBtn: {
    marginTop: theme.spacing(1)
  },
}));

const mutation = graphql`
  mutation UploadProfilePictureMutation($fileType: String!, $contentLength: Int!) {
    profileImageUploadRequest(
      input: { fileType: $fileType, contentLength: $contentLength }
    ) {
      successToken
      url
    }
  }
`;

type Props = {
  title?: string;
  url?: string;
  onUploaded: (token: string, url: string) => void;
  acceptType?: string;
};

function UploadProfilePicture({
  title = 'Upload Image',
  url,
  onUploaded,
  acceptType = 'image/*',
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  
  const inputRef = React.createRef<HTMLInputElement>();
  const [uploadText, setUploadText] = React.useState(title);
  React.useEffect(() => {
    setUploadText(title);
  }, [title]);

  const uploadChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const mutationName = mutation.default.params.name;
    if (!mutationName) {
      console.error('UploadFile: Could not find mutation name');
      return;
    }
    // Attempt to get upload request
    if (!evt.target.files) {
      console.error('UploadFile: Could not find target file');
      return;
    }
    const file = evt.target.files[0];
    const split = file.name.split('.');
    const fType = split[split.length - 1];
    setUploadText('Uploading...');
    try {
      commitMutation(environment, {
        mutation,
        variables: {
          fileType: fType,
          contentLength: file.size
        },
        onCompleted: async (
          response: {
            profileImageUploadRequest: {
              url: string,
              successToken: string,
            }
          },
          errors: GraphError[]
        ) => {
          if (errors) {
            // Display error
            console.log(`${errors[0]?.extensions?.message}`);
            return;
          }
          console.log('Response received from server.', response, errors);
          const data = response?.profileImageUploadRequest;
          // Upload to S3
          const uploadResp = await fetch(data.url, {
            method: 'PUT',
            body: file
          });

          if (uploadResp.status !== 200) {
            console.log('Unable to upload');
            setUploadText('Unable to upload, try again');
            return;
          }
          const fileName = file.name.length > 20 ? file.name.slice(0,10) + '...' + file.name.slice(-10) : file.name;
          setUploadText(fileName);

          onUploaded(data.successToken, URL.createObjectURL(file));
        },
        onError: (err) => console.error(err)
      });
    } catch (err) {
      console.error('UploadFile: ' + err);
      setUploadText('Unable to upload, please try again');
    }
  };

  const randomId = `random-${Math.random()}`;

  return (
    <div className={classes.profilePicContainer}>
        <Icon
            name="EditPicPencil"
            size={30}
            className={classes.editPicPencilIcon}
        />
        {url ? (
            <img
              src={url}
              className={classes.profilePic}
            />
        ) : (
        <Icon
            name="EditDelegateProfilePic_Default"
            pointer
            size={100}
            onClick={() => inputRef.current?.click()}
        />
        )}
        <input
        accept={acceptType}
        className={classes.input}
        id={'contained-button-file-' + randomId}
        onChange={uploadChange}
        type="file"
        ref={inputRef}
      />
      <label htmlFor={'contained-button-file-' + randomId}>
        <Button
          archetype="grey"
          className={classes.editPhotoBtn}
          onClick={() => inputRef.current?.click()}
        >
          {uploadText}
        </Button>
        </label>
    </div>
  );
}

export default UploadProfilePicture;

